import React, { useState } from 'react';
import { Button, Progress, Divider, Accordion, AccordionItem } from "@nextui-org/react";
import { Icon } from '@iconify/react';
import { useGenerateImageMutation } from '@circuitry-ai/doc-data';
import { CtyShowToast, UploadedFileType, CtySpinner, convertToLocalDayMonthYear, useGoogleMapsApiKey } from '@circuitry-ai/doc-ui';
import { GoogleMap, Marker, LoadScript } from '@react-google-maps/api';

interface ImageFraudResult {
    classification_results: string;
    tampering_results: string;
    authentication_score: number;
    similarity_results: Array<{
        score: number;
        file_name: string;
        id: string;
        source_id: string;
        source_path: string;
    }>;
    se_ai_generated_results: {
        status: string;
        request: {
            id: string;
            timestamp: number;
            operations: number;
        };
        type: {
            ai_generated: number;
        };
        media: {
            id: string;
            uri: string;
        };
        confidence_score: number;
        ai_generation: boolean;
    };
    modified_ai_generation_results: {
        status: string;
        request: {
            id: string;
            timestamp: number;
            operations: number;
        };
        type: {
            ai_generated: number;
        };
        media: {
            id: string;
            uri: string;
        };
        confidence_score: number;
        ai_generation: boolean;
    };
    web_detection_results: {
        direct_matches: string[];
        similar_matches: string[];
    };
    duplicate_results: Array<{
        file_name: string;
        id: string;
        source_id: string;
        source_path: string;
        hash_diff: number;
    }>;
    metadata_results: {
        modified_GPS?: {
            latitude: number;
            longitude: number;
        };
        Make?: string;
        Model?: string;
        Software?: string;
        LensModel?: string;
        modified_metadata?: any;
        [key: string]: any;
    };
}

interface ImageFraudCheckerProps {
    uploadedFile: UploadedFileType;
    previewUrl: string;
    apiResponse?: any;
    isLoading?: boolean;
    isUploading?: boolean;
    onRemoveFile: () => void;
}

export function ImageFraudChecker({ uploadedFile, previewUrl, apiResponse, isLoading = false, isUploading = false, onRemoveFile }: ImageFraudCheckerProps) {
    const [isSimilarImageLoading, setIsSimilarImageLoading] = useState<string>('');
    const [generateImage] = useGenerateImageMutation();
    const { apiKey: googleMapsApiKey, hasApiKey, loading: mapsKeyLoading } = useGoogleMapsApiKey();

    const handleSimilarImageClick = async (match: ImageFraudResult['similarity_results'][0] | ImageFraudResult['duplicate_results'][0], index: number, type: 'duplicate' | 'similarity') => {
        try {
            setIsSimilarImageLoading(match.id + type);
            const ImageResponse = await generateImage({
                s3_paths: [match?.source_path],
            }).unwrap();
            ImageResponse?.data?.[0]?.presigned_url && window.open(ImageResponse?.data?.[0]?.presigned_url, '_blank');
            setIsSimilarImageLoading('');
        } catch (error) {
            console.error('Error fetching similar image:', error);
            CtyShowToast('error', 'Failed to load similar image');
            setIsSimilarImageLoading('');
        }
    };

    // Helper functions to reduce code duplication
    const mapConfig = {
        containerStyle: { width: '100%', height: '100%' },
        zoom: 15
    };

    const renderGoogleMap = (lat: number, lng: number) => (
        <GoogleMap
            mapContainerStyle={mapConfig.containerStyle}
            center={{ lat, lng }}
            zoom={mapConfig.zoom}
        >
            <Marker position={{ lat, lng }} />
        </GoogleMap>
    );

    const renderImageButton = (match: ImageFraudResult['similarity_results'][0] | ImageFraudResult['duplicate_results'][0], index: number, type: 'duplicate' | 'similarity') => (
        <Button
            key={match.id}
            variant="light"
            size="sm"
            color="primary"
            onClick={() => handleSimilarImageClick(match, index, type)}
            isLoading={isSimilarImageLoading === match.id + type}
            className="text-xs"
        >
            {match.file_name}
        </Button>
    );

    const renderSectionTitle = (title: string) => (
        <span className="font-semibold" style={{ color: 'var(--cty-primary-accent)' }}>{title}</span>
    );

    const renderInfoRow = (label: string, value: string | React.ReactNode) => (
        <div className="flex items-center justify-between">
            {renderSectionTitle(`${label}:`)}
            <span className="text-default-700">{value}</span>
        </div>
    );

    const renderImageList = (items: Array<ImageFraudResult['similarity_results'][0] | ImageFraudResult['duplicate_results'][0]>, type: 'duplicate' | 'similarity', emptyMessage: string = 'No similar images found') => (
        <div className="flex flex-wrap gap-2">
            {items && items.length > 0 ?
                items.map((match, index) => renderImageButton(match, index, type))
                : <span className="text-default-500 text-sm">{emptyMessage}</span>
            }
        </div>
    );

    const renderMap = (gpsData: ImageFraudResult['metadata_results']['modified_GPS']) => {
        const lat = gpsData?.latitude || 40.573339204027945;
        const lng = gpsData?.longitude || -74.11933042781544;
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

        if (mapsKeyLoading) {
            return (
                <div className='w-full h-[200px] mt-2 rounded-lg overflow-hidden border bg-default-100 flex items-center justify-center'>
                    <CtySpinner />
                </div>
            );
        }

        if (!hasApiKey) {
            return (
                <div className='w-full h-[200px] mt-2 rounded-lg overflow-hidden border bg-default-100 flex items-center justify-center'>
                    <div className="text-center text-default-500">
                        <Icon icon="material-symbols:map" className="w-12 h-12 mb-2 mx-auto" />
                        <div className="text-sm">Map unavailable</div>
                        <div className="text-xs">Google Maps API key required</div>
                        <div className="text-xs text-primary mt-1">
                            Coordinates: {lat.toFixed(6)}, {lng.toFixed(6)}
                        </div>
                    </div>
                </div>
            );
        }

        // Check if Google Maps is already loaded
        const isGoogleMapsLoaded = typeof window !== 'undefined' && window.google && window.google.maps;

        return (
            <div className='w-full h-[200px] mt-2 rounded-lg overflow-hidden border'>
                {isGoogleMapsLoaded ? (
                    renderGoogleMap(lat, lng)
                ) : (
                    <LoadScript googleMapsApiKey={googleMapsApiKey}>
                        {renderGoogleMap(lat, lng)}
                    </LoadScript>
                )}
            </div>
        );
    };

    const getAuthenticationScore = (fraudResult: ImageFraudResult) => {
        if (!fraudResult) return 'N/A';
        const confidence = Math.round(fraudResult?.authentication_score || 0);
        return confidence + '%';
    };

    const convertExifToISO = (exifDate: string): string => {
        // Convert "2015:02:23 10:11:50" to "2015-02-23T10:11:50"
        return exifDate.replace(' ', 'T').replace(/:/g, (match, index) => {
            // Replace colons with dashes for date part, keep colons for time part
            if (index < 10) return '-';
            return ':';
        });
    };

    const getDateTimestamp = (fraudResult: ImageFraudResult) => {
        if (!fraudResult) return null;
        const timestamp = fraudResult?.metadata_results?.DateTime;
        if (timestamp !== undefined && timestamp !== null && timestamp !== '') {
            try {
                // Convert EXIF format to ISO format
                const isoDate = convertExifToISO(timestamp);
                return convertToLocalDayMonthYear(isoDate);
            } catch (error) {
                console.error("Error converting timestamp:", error);
                return timestamp; // Return the original string if conversion fails
            }
        }
        return null;
    };

    const getAuthenticationStatus = (fraudResult: ImageFraudResult) => {
        const score = getAuthenticationScore(fraudResult);
        if (score === 'N/A') return 'Unknown';
        const numericScore = parseInt(score);
        return numericScore >= 70 ? 'Passed!' : 'Failed';
    };

    const renderImageFraudResults = (fraudResult: ImageFraudResult) => {
        return (
            <div className="space-y-4">
                {/* Authentication Score */}
                <div className="text-center">
                    <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--cty-primary-accent)' }}>Authentication Score</h2>
                    <div className="text-6xl font-bold text-foreground mb-2">{getAuthenticationScore(fraudResult)}</div>
                    <div className="w-full max-w-sm mx-auto mb-3">
                        <Progress
                            value={parseInt(getAuthenticationScore(fraudResult)) || 0}
                            maxValue={100}
                            color={parseInt(getAuthenticationScore(fraudResult)) >= 70 ? 'success' : 'danger'}
                            className="h-2"
                        />
                    </div>
                    <div className={`text-sm font-medium ${parseInt(getAuthenticationScore(fraudResult)) >= 70 ? 'text-success' : 'text-danger'}`}>
                        {getAuthenticationStatus(fraudResult)}
                    </div>
                    {getDateTimestamp(fraudResult) && (
                        <div className="flex items-center gap-2 justify-center mt-1">
                            <span className="text-sm text-default-500">
                                {getDateTimestamp(fraudResult)}
                            </span>
                        </div>
                    )}
                </div>

                <Divider />

                {/* Classification Results */}
                {renderInfoRow('Image Classification', fraudResult.classification_results || 'Unknown')}

                <Divider />

                {/* Duplicate Image */}
                <div className="flex  flex-col gap-2">
                    <div className="flex flex-row items-center justify-between w-full">
                        {renderSectionTitle('Duplicate image:')}
                        <div className="text-default-700">{fraudResult.duplicate_results?.length > 0 ? 'Positive' : 'Negative'}</div>
                    </div>
                    {renderImageList(fraudResult.duplicate_results, 'duplicate')}
                </div>

                <Divider />

                {/* AI Generation */}
                {renderInfoRow('AI Generation',
                    fraudResult.modified_ai_generation_results?.ai_generation || fraudResult.se_ai_generated_results?.ai_generation ? 'Positive' : 'Negative'
                )}

                <Divider />

                {/* Tampered Percentage */}
                {renderInfoRow('Tampered Percentage', fraudResult.tampering_results || 'N/A')}

                <Divider />

                {/* Similar Image Matches */}
                <div>
                    <div className="font-semibold mb-2" style={{ color: 'var(--cty-primary-accent)' }}>Similar Image Matches:</div>
                    {renderImageList(fraudResult.similarity_results, 'similarity')}
                </div>

                <Divider />

                {/* Web Detection */}
                {renderInfoRow('Web Detection',
                    fraudResult.web_detection_results?.direct_matches?.length > 0 ? 'Positive' : 'Negative'
                )}

                <Divider />


                {/* Geo Information with Map */}
                {fraudResult.metadata_results?.modified_GPS && (
                    <>
                        <div>
                            <div className="font-semibold mb-2" style={{ color: 'var(--cty-primary-accent)' }}>Geo Information</div>
                            <div className="text-sm text-default-500 mb-3">
                                <div>City: Chicago</div>
                                <div>State: Illinois</div>
                                <div>Zipcode: 60602</div>
                            </div>
                            {renderMap(fraudResult.metadata_results.modified_GPS)}
                        </div>
                        <Divider />
                    </>
                )}

                {/* Metadata Results */}
                {fraudResult.metadata_results && Object.keys(fraudResult.metadata_results).length > 0 && (
                    <>
                        <div>
                            <Accordion>
                                <AccordionItem
                                    key="metadata"
                                    aria-label="Metadata Information"
                                    title={<span className="font-semibold" style={{ color: 'var(--cty-primary-accent)' }}>Metadata Information</span>}
                                >
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {Object.entries(fraudResult.metadata_results)
                                            .filter(([key, value]) =>
                                                key !== 'modified_GPS' &&
                                                key !== 'modified_metadata' &&
                                                value !== null &&
                                                value !== undefined &&
                                                value !== ''
                                            )
                                            .map(([key, value]) => (
                                                <div key={key} className="p-2 bg-default-100 rounded">
                                                    <div className="text-sm break-all">
                                                        <span className="font-medium" style={{ color: 'var(--cty-primary-accent)' }}>{key}:</span>
                                                        <span className="text-default-500 ml-1">
                                                            {typeof value === 'object' && value !== null ? (
                                                                Array.isArray(value) ?
                                                                    `[${value.join(', ')}]` :
                                                                    JSON.stringify(value, null, 2)
                                                            ) : (
                                                                typeof value === 'string' && value.length > 200 ?
                                                                    `${value.substring(0, 200)}...` :
                                                                    String(value)
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="h-full flex flex-col bg-default-100">
            {/* Header */}
            <div className="px-6 py-4 bg-content1 border-b">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold" style={{ color: 'var(--cty-primary-accent)' }}>Image Analysis</h1>
                    </div>
                    <Button variant="light" size="sm" className="text-foreground" onClick={onRemoveFile}>
                        Close
                    </Button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Side - Image Preview */}
                <div className="w-1/2 p-6 bg-content1 border-r flex-shrink-0">
                    <div className="h-full flex flex-col">
                        <div className="relative flex-1 bg-default-100 rounded-lg overflow-hidden">
                            <img
                                src={previewUrl}
                                alt="Uploaded image"
                                className="w-full h-full object-contain"
                            />

                            {/* File Name Overlay */}
                            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                                <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md">
                                    <div className="text-sm font-medium text-default-700">{uploadedFile.name}</div>
                                </div>
                                <Button
                                    isIconOnly
                                    variant="light"
                                    size="sm"
                                    onClick={onRemoveFile}
                                    className="bg-background/90 backdrop-blur-sm hover:bg-content1 rounded-full w-8 h-8 min-w-8 flex items-center justify-center shadow-md"
                                >
                                    <Icon icon="material-symbols:close" className="text-default-500" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Results Panel */}
                <div className="flex-1 p-6 overflow-y-auto">
                    {isUploading ? (
                        <div className="h-full flex flex-col items-center justify-center text-default-500">
                            <CtySpinner size="lg" />
                            <h3 className="text-lg font-semibold" style={{ color: 'var(--cty-primary-accent)' }}>Uploading Image</h3>
                        </div>
                    ) : isLoading ? (
                        <div className="h-full flex flex-col items-center justify-center text-default-500">
                            <CtySpinner size="lg" />
                            <h3 className="text-lg font-semibold" style={{ color: 'var(--cty-primary-accent)' }}>Processing Image</h3>
                        </div>
                    ) : apiResponse?.length > 0 && apiResponse[0] ? (
                        renderImageFraudResults(apiResponse[0])
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-default-500">
                            <Icon icon="material-symbols:image-search" className="w-16 h-16 mb-4" />
                            <p className="text-center">
                                click on execute to start the analysis
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}